import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { EscapeRoomContainer } from '@/features/escape-room/EscapeRoomContainer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import fileVaultData from '@/data/rooms/file-vault.json';
import editorsChamberData from '@/data/rooms/editors-chamber.json';
import detectivesCaseData from '@/data/rooms/detectives-case.json';
import treasureQuestData from '@/data/rooms/treasure-quest.json';

export default function EscapeRoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [roomData, setRoomData] = useState<any>(null);

  // Technical track - coding and terminal skills required
  const technicalRooms = [
    {
      id: 'file-vault',
      name: 'The File Vault',
      theme: 'Mystery Investigation',
      difficulty: 'Beginner',
      time: '15 min',
      puzzles: 3,
      xp: 200,
      unlocked: true,
      description: 'Use Glob, Grep, and Read to find hidden codes',
    },
    {
      id: 'editors-chamber',
      name: "The Editor's Chamber",
      theme: 'Code Restoration',
      difficulty: 'Intermediate',
      time: '20 min',
      puzzles: 3,
      xp: 250,
      unlocked: true,
      description: 'Fix syntax errors and refactor code',
    },
  ];

  // Non-technical track - document analysis with Claude AI
  const nonTechnicalRooms = [
    {
      id: 'detectives-case',
      name: "The Detective's Case",
      theme: 'Document Analysis Mystery',
      difficulty: 'Beginner',
      time: '30 min',
      puzzles: 3,
      xp: 300,
      unlocked: true,
      description: 'Analyze police reports and evidence with Claude AI',
    },
    {
      id: 'treasure-quest',
      name: 'The Treasure Quest',
      theme: 'Historical Document Analysis',
      difficulty: 'Beginner',
      time: '30 min',
      puzzles: 3,
      xp: 350,
      unlocked: true,
      description: 'Decipher historical documents to find treasure',
    },
  ];

  useEffect(() => {
    if (roomId === 'file-vault') {
      setRoomData(fileVaultData);
    } else if (roomId === 'editors-chamber') {
      setRoomData(editorsChamberData);
    } else if (roomId === 'detectives-case') {
      setRoomData(detectivesCaseData);
    } else if (roomId === 'treasure-quest') {
      setRoomData(treasureQuestData);
    }
  }, [roomId]);

  // Room selection screen
  if (!roomId) {
    return (
      <div className="min-h-screen p-8 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="mb-4"
            >
              ← Back to Home
            </Button>
            <h1 className="text-4xl font-bold mb-2 text-primary-400">
              Escape Room Mode
            </h1>
            <p className="text-primary-200">
              Learn through narrative-driven puzzle adventures
            </p>
          </div>

          {/* Technical Track */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">💻</span>
              <div>
                <h2 className="text-2xl font-bold text-primary-400">Technical Escape Rooms</h2>
                <p className="text-sm text-primary-300">For developers - use coding tools and terminal commands</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {technicalRooms.map((room) => (
                <Card
                  key={room.id}
                  variant={room.unlocked ? 'elevated' : 'default'}
                  className={
                    room.unlocked
                      ? 'hover:border-primary-500/50 cursor-pointer transition-all duration-300 hover:scale-105'
                      : 'opacity-50 cursor-not-allowed'
                  }
                  onClick={() => room.unlocked && navigate(`/escape-room/${room.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-4xl">🔐</div>
                      {!room.unlocked && <span className="text-xl">🔒</span>}
                    </div>
                    <Badge variant="info" size="sm" className="mb-2">
                      {room.theme}
                    </Badge>
                    <CardTitle className="text-lg">{room.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-primary-300 mb-3">{room.description}</p>
                    <div className="space-y-2 mb-4 text-sm text-primary-300">
                      <div className="flex justify-between">
                        <span>Difficulty:</span>
                        <Badge
                          variant={
                            room.difficulty === 'Beginner'
                              ? 'success'
                              : room.difficulty === 'Intermediate'
                              ? 'warning'
                              : 'error'
                          }
                          size="sm"
                        >
                          {room.difficulty}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Time Limit:</span>
                        <span className="font-semibold text-warning-400">{room.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Puzzles:</span>
                        <span>{room.puzzles}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Reward:</span>
                        <span className="text-success-400 font-semibold">{room.xp} XP</span>
                      </div>
                    </div>
                    {room.unlocked ? (
                      <Button variant="primary" size="sm" className="w-full">
                        Enter Room →
                      </Button>
                    ) : (
                      <p className="text-sm text-primary-200/50">
                        Complete previous rooms to unlock
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Non-Technical Track */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">📄</span>
              <div>
                <h2 className="text-2xl font-bold text-primary-400">Non-Technical Escape Rooms</h2>
                <p className="text-sm text-primary-300">For everyone - no coding required, use Claude AI for document analysis</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {nonTechnicalRooms.map((room) => (
                <Card
                  key={room.id}
                  variant={room.unlocked ? 'elevated' : 'default'}
                  className={
                    room.unlocked
                      ? 'hover:border-primary-500/50 cursor-pointer transition-all duration-300 hover:scale-105'
                      : 'opacity-50 cursor-not-allowed'
                  }
                  onClick={() => room.unlocked && navigate(`/escape-room/${room.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-4xl">🔐</div>
                      {!room.unlocked && <span className="text-xl">🔒</span>}
                    </div>
                    <Badge variant="success" size="sm" className="mb-2">
                      {room.theme}
                    </Badge>
                    <CardTitle className="text-lg">{room.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-primary-300 mb-3">{room.description}</p>
                    <div className="space-y-2 mb-4 text-sm text-primary-300">
                      <div className="flex justify-between">
                        <span>Difficulty:</span>
                        <Badge
                          variant={
                            room.difficulty === 'Beginner'
                              ? 'success'
                              : room.difficulty === 'Intermediate'
                              ? 'warning'
                              : 'error'
                          }
                          size="sm"
                        >
                          {room.difficulty}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Time Limit:</span>
                        <span className="font-semibold text-warning-400">{room.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Puzzles:</span>
                        <span>{room.puzzles}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Reward:</span>
                        <span className="text-success-400 font-semibold">{room.xp} XP</span>
                      </div>
                    </div>
                    {room.unlocked ? (
                      <Button variant="primary" size="sm" className="w-full">
                        Enter Room →
                      </Button>
                    ) : (
                      <p className="text-sm text-primary-200/50">
                        Complete previous rooms to unlock
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!roomData) {
    return (
      <div className="min-h-screen p-8 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-primary-200">Loading room...</p>
        </div>
      </div>
    );
  }

  // Room content
  return (
    <div className="min-h-screen p-8 bg-black">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/escape-room')}>
          ← Back to Rooms
        </Button>
      </div>
      <EscapeRoomContainer roomData={roomData} />
    </div>
  );
}
